import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Check, Crown, Sparkles, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const SubscriptionPage = () => {
  const { token, logout, checkSubscription } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  
  const sessionId = searchParams.get('session_id');

  const fetchSubscriptionStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(response.data);
      
      // If active, redirect to home
      if (response.data.is_active) {
        navigate('/');
      }
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  }, [token, navigate]);

  const pollPaymentStatus = useCallback(async (sid, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please contact support.');
      navigate('/subscription');
      return;
    }

    setCheckingStatus(true);

    try {
      const response = await axios.get(`${API}/subscription/checkout/status/${sid}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Check if payment successful OR subscription is trialing
      if (response.data.payment_status === 'paid' || response.data.subscription_status === 'trialing' || response.data.subscription_status === 'active') {
        // Show success screen first
        setCheckingStatus(false);
        setPaymentSuccess(true);
        
        // Update subscription status in context
        await checkSubscription();
        
        // Wait for animation, then redirect
        setTimeout(() => {
          navigate('/');
        }, 2500);
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        navigate('/subscription');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    }
  }, [token, navigate, checkSubscription]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      fetchSubscriptionStatus();
    }
  }, [sessionId, pollPaymentStatus, fetchSubscriptionStatus]);

  const handleSubscribe = async (plan) => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/subscription/checkout`,
        {
          plan,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe Checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to start checkout. Please try again.');
      setLoading(false);
    }
  };

  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-pink-50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <Card className="w-full max-w-md border-0 shadow-2xl bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl text-center p-8">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <Check className="w-10 h-10 text-green-600" />
            </motion.div>
            <motion.h2
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-2xl font-outfit font-bold text-green-600 mb-2"
            >
              Payment Successful!
            </motion.h2>
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-muted-foreground"
            >
              Welcome to Business Organizational Bestie!
            </motion.p>
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="text-sm text-muted-foreground mt-4"
            >
              Redirecting you to your dashboard...
            </motion.p>
          </Card>
        </motion.div>
      </div>
    );
  }

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-pink-50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-0 shadow-2xl bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl text-center p-8">
          <Loader2 className="w-12 h-12 animate-spin text-violet-600 mx-auto mb-4" />
          <h2 className="text-xl font-outfit font-bold">Verifying Payment...</h2>
          <p className="text-muted-foreground mt-2">Please wait while we confirm your subscription.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-pink-50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto w-20 h-20 mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_productivepro-10/artifacts/sm0xqhz6_IMG_3253.png" 
              alt="B.O.B. Logo" 
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-3xl font-outfit font-bold bg-gradient-to-r from-violet-600 to-pink-600 bg-clip-text text-transparent">
            Choose Your Plan
          </h1>
          <p className="text-muted-foreground mt-2">
            Add your card to start your 3-day free trial. You won't be charged until the trial ends, and you can cancel anytime before then.
          </p>
          {subscriptionStatus?.trial_ends_at && !subscriptionStatus?.is_active && (
            <p className="text-red-500 mt-2 font-medium">
              Your trial has expired. Subscribe to continue using B.O.B.
            </p>
          )}
        </div>

        {/* Plans */}
        <div className="grid md:grid-cols-2 gap-6 max-w-2xl mx-auto">
          {/* Monthly Plan */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="border-2 border-violet-200 dark:border-violet-800 hover:border-violet-400 transition-colors h-full" data-testid="monthly-plan-card">
              <CardHeader className="text-center pb-2">
                <div className="mx-auto w-12 h-12 bg-violet-100 dark:bg-violet-900/30 rounded-full flex items-center justify-center mb-2">
                  <Sparkles className="w-6 h-6 text-violet-600" />
                </div>
                <CardTitle className="text-xl text-violet-700 dark:text-violet-300">Monthly</CardTitle>
                <CardDescription>Perfect for trying out B.O.B.</CardDescription>
              </CardHeader>
              <CardContent className="text-center space-y-4">
                <div className="text-4xl font-bold text-violet-600">
                  $14.99<span className="text-lg text-muted-foreground font-normal">/month</span>
                </div>
                <ul className="space-y-2 text-left">
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-500" /> All productivity features
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-500" /> Unlimited goals & tasks
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-500" /> Contact management
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-500" /> Calendar & reminders
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-500" /> Data export/import
                  </li>
                </ul>
                <Button 
                  onClick={() => handleSubscribe('monthly')}
                  disabled={loading}
                  className="w-full bg-violet-600 hover:bg-violet-700"
                  data-testid="subscribe-monthly-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Start Free Trial
                </Button>
              </CardContent>
            </Card>
          </motion.div>

          {/* Yearly Plan */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="border-2 border-amber-400 dark:border-amber-600 relative overflow-hidden h-full" data-testid="yearly-plan-card">
              <div className="absolute top-0 right-0 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                BEST VALUE
              </div>
              <CardHeader className="text-center pb-2">
                <div className="mx-auto w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mb-2">
                  <Crown className="w-6 h-6 text-amber-600" />
                </div>
                <CardTitle className="text-xl text-amber-700 dark:text-amber-300">Yearly</CardTitle>
                <CardDescription>Save over $30 per year!</CardDescription>
              </CardHeader>
              <CardContent className="text-center space-y-4">
                <div className="text-4xl font-bold text-amber-600">
                  $149.99<span className="text-lg text-muted-foreground font-normal">/year</span>
                </div>
                <p className="text-sm text-green-600 font-medium">Save $29.89 vs monthly</p>
                <ul className="space-y-2 text-left">
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-500" /> All productivity features
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-500" /> Unlimited goals & tasks
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-500" /> Contact management
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-500" /> Calendar & reminders
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-500" /> Data export/import
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-amber-500" /> Priority support
                  </li>
                </ul>
                <Button 
                  onClick={() => handleSubscribe('yearly')}
                  disabled={loading}
                  className="w-full bg-amber-500 hover:bg-amber-600"
                  data-testid="subscribe-yearly-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Start Free Trial
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 space-y-2">
          <p className="text-sm text-muted-foreground">
            3-day free trial, then $14.99/month or $149.99/year • Card required • Auto-charged when the trial ends • Cancel anytime • Secure payment via Stripe
          </p>
          <Button 
            variant="ghost" 
            onClick={logout}
            className="text-muted-foreground hover:text-foreground"
            data-testid="logout-from-subscription"
          >
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
